import React from 'react';
import cx from 'classnames';
import Icon, { IconName } from 'components/icon/icon';
import { P2 } from 'components/typography';
import A11yVisuallyHidden from 'components/a11y/a11y-visually-hidden';
import FocusTrap from 'focus-trap-react';

type Props = {
    onChange?: (value: string) => void;
    options: {
        title?: string;
        value?: string;
        href?: string;
    }[];
    defaultValue?: string;
    a11y?: {
        label: string;
    };
};

const Select: React.FC<Props> = ({ onChange, options, defaultValue, a11y }) => {
    const [value, setValue] = React.useState(() => {
        if (!defaultValue) return options[0];

        return options.find((item) => item.value === defaultValue);
    });
    const [isOpen, setOpen] = React.useState(false);
    const [direction, setDirection] = React.useState(true);

    const dropdownRef = React.useRef<HTMLUListElement>(null);
    const selectRef = React.useRef<HTMLDivElement>(null);
    const selectLabelRef = React.useRef<HTMLButtonElement>(null);

    const handleOptionClick = (item: object) => {
        setValue(item);

        setOpen(false);
        if (onChange) {
            //@ts-ignore
            onChange(item.value);
        }
    };

    const getDropdownHeight = React.useCallback(() => {
        const el = dropdownRef.current;
        let dropdownHeight: number = 0;
        if (el) {
            el.style.display = 'block';
            dropdownHeight = el.getBoundingClientRect().height;
            el.style.display = 'none';
        }

        return dropdownHeight;
    }, [dropdownRef]);

    const handleOpen = () => {
        if (!selectLabelRef.current) return;

        const dropdownHeight = getDropdownHeight();
        const selectElementRect: DOMRect = selectLabelRef.current.getBoundingClientRect();

        const totalHeight = dropdownHeight + selectElementRect.height;

        const hasEnoughSpace = window.innerHeight - selectElementRect.top > totalHeight;

        setDirection(hasEnoughSpace);

        setOpen(!isOpen);
    };

    React.useEffect(() => {
        getDropdownHeight();
    });

    return (
        <FocusTrap
            focusTrapOptions={{
                escapeDeactivates: true,
                onDeactivate: () => setOpen(false),
                initialFocus: false,
                allowOutsideClick: true,
            }}
            active={isOpen}
        >
            <div
                ref={selectRef}
                className={cx({
                    select: true,
                    'is-opened': isOpen,
                    'direction-top': !direction,
                })}
            >
                <button
                    onClick={handleOpen}
                    className="select-label"
                    ref={selectLabelRef}
                    aria-expanded={Boolean(isOpen)}
                >
                    <A11yVisuallyHidden>{a11y?.label}</A11yVisuallyHidden>

                    <span className="select-label__text">
                        <P2 color="white">{value?.title}</P2>
                    </span>

                    <Icon name={IconName.ChevronUp} size="xsm" />
                </button>
                <ul className="select-dropdown" ref={dropdownRef} role="list">
                    {options
                        ?.filter((item) => item.value !== value?.value)
                        .map((item) => {
                            const Tag = item.href ? 'a' : 'button';

                            return (
                                <li key={item.title}>
                                    <Tag
                                        data-value={item.value}
                                        href={item.href}
                                        onClick={(event) => {
                                            event.preventDefault();

                                            handleOptionClick(item);
                                        }}
                                        className="select-dropdown__item"
                                        type={!item.href ? 'button' : undefined}
                                    >
                                        <P2 color="white">{item.title}</P2>
                                    </Tag>
                                </li>
                            );
                        })}
                </ul>
            </div>
        </FocusTrap>
    );
};

export default Select;
